import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import ElementPlus from 'element-plus'
import { UserFilled, User, Collection, Document, Money, ArrowUp, HomeFilled, Calendar, Clock, Setting, Picture, Search, Edit, Delete, Plus, Refresh, RefreshRight, ArrowLeft, Check, Close, Loading, AlarmClock, Fold, Expand, ArrowDown, SwitchButton, View, Message, Lock, Phone, Lightning, InfoFilled } from '@element-plus/icons-vue'

import App from './App.vue'
import router from './router'

import 'element-plus/dist/index.css'
import '@/styles/index.scss'
import '@/assets/fonts/iconfont.css'

import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import zhMessages from '@/locales/zh.json'
import enMessages from '@/locales/en.json'
import ugMessages from '@/locales/ug.json'
import { imageCacheUtil } from '@/utils/image-cache'

const app = createApp(App)

// Configure global error handler
if (import.meta.env.PROD) {
  // In production, log errors but don't show to users
  // Suppress console in production
  const noop = () => {}
  console.log = noop
  console.warn = noop
  console.error = noop
  console.debug = noop
} else {
  // In development, show all warnings
  app.config.warnHandler = (msg, instance, trace) => {
    console.warn(`[Vue Warn]: ${msg}`, trace)
  }
}
// Register only commonly used Element Plus icons
const commonIcons = {
  UserFilled, User, Collection, Document, Money, ArrowUp,
  HomeFilled, Calendar, Clock, Setting, Picture,
  Search, Edit, Delete, Plus, Refresh, RefreshRight, ArrowLeft,
  Check, Close, Loading,
  AlarmClock, Fold, Expand, ArrowDown, SwitchButton,
  View, Message, Lock, Phone, Lightning, InfoFilled
}

for (const [key, component] of Object.entries(commonIcons)) {
  app.component(key, component)
}

// Get saved language
const getSavedLanguage = () => {
  try {
    const saved = localStorage.getItem('yolal-language')
    return saved || 'zh'
  } catch {
    return 'zh'
  }
}

// Create i18n with all translations pre-loaded for instant rendering
const i18n = createI18n({
  legacy: false,
  locale: getSavedLanguage(),
  fallbackLocale: 'zh',
  messages: {
    zh: zhMessages,
    en: enMessages,
    ug: ugMessages
  },
  silentFallbackWarn: true,
  missingWarn: false
})

app.use(createPinia())
app.use(router)
app.use(ElementPlus)
app.use(i18n)

// Initialize stores
const appStore = useAppStore()
const userStore = useUserStore()

// Initialize app store synchronously (lightweight)
appStore.init()

// Load session synchronously to ensure router guard works correctly
userStore.loadSession()

// Clean up expired image cache entries on startup (non-blocking)
setTimeout(() => {
  try {
    const stats = imageCacheUtil.getCacheStats()
    console.log('[ImageCache] Stats:', stats)
    
    // If too many expired entries or using too much space, clean up
    if (stats.expiredEntries > 10 || stats.localStorageUsedPercent > 50) {
      const cleaned = imageCacheUtil.cleanupExpired()
      console.log(`[ImageCache] Cleaned ${cleaned} expired entries`)
    }
  } catch (error) {
    console.error('[ImageCache] Cleanup error:', error)
  }
}, 2000) // Run after 2 seconds to not block initial load

app.mount('#app')

// Service Worker is now handled by vite-plugin-pwa automatically
// The plugin registers the SW and handles updates
// Add diagnostic logging for PWA debugging
if ('serviceWorker' in navigator) {
  console.log('[PWA] Service Worker API available');
  
  // Check if service worker is registered
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    console.log('[PWA] Service Worker registrations:', registrations.length);
    registrations.forEach((reg, index) => {
      console.log(`[PWA] Registration ${index}:`, {
        scope: reg.scope,
        active: !!reg.active,
        installing: !!reg.installing,
        waiting: !!reg.waiting
      });
    });
  }).catch(err => {
    console.error('[PWA] Error checking SW registrations:', err);
  });
} else {
  console.warn('[PWA] Service Worker API NOT available');
}

// Hide loading screen after app is mounted
setTimeout(() => {
  const loadingScreen = document.getElementById('loading-screen')
  if (loadingScreen) {
    loadingScreen.classList.add('fade-out')
    setTimeout(() => {
      loadingScreen.remove()
    }, 300)
  }
}, 100)